import { useState, useMemo } from 'react';
import { ActivityItem, SortOption } from '../types';

interface UseActivitySortingProps {
  activities: ActivityItem[];
}

interface UseActivitySortingReturn {
  sortedActivities: ActivityItem[];
  sortOptions: SortOption[];
  selectedSort: string;
  setSelectedSort: (value: string) => void;
}

export const useActivitySorting = ({
  activities,
}: UseActivitySortingProps): UseActivitySortingReturn => {
  const [selectedSort, setSelectedSort] = useState<string>('date-desc');

  const sortOptions: SortOption[] = [
    { label: 'Most Recent First', value: 'date-desc' },
    { label: 'Oldest First', value: 'date-asc' },
    { label: 'Name A-Z', value: 'name-asc' },
    { label: 'Name Z-A', value: 'name-desc' },
    { label: 'Status', value: 'status' }
  ];

  const sortedActivities = useMemo(() => {
    const activitiesCopy = [...activities];
    
    switch (selectedSort) {
      case 'date-desc':
        return activitiesCopy.sort((a, b) => {
          const dateA = new Date(a.completedAt || a.createdAt || a.date);
          const dateB = new Date(b.completedAt || b.createdAt || b.date);
          return dateB.getTime() - dateA.getTime();
        });
      
      case 'date-asc':
        return activitiesCopy.sort((a, b) => {
          const dateA = new Date(a.completedAt || a.createdAt || a.date);
          const dateB = new Date(b.completedAt || b.createdAt || b.date);
          return dateA.getTime() - dateB.getTime();
        });
      
      case 'name-asc':
        return activitiesCopy.sort((a, b) => 
          a.reportName.localeCompare(b.reportName)
        );
      
      case 'name-desc':
        return activitiesCopy.sort((a, b) => 
          b.reportName.localeCompare(a.reportName)
        );
      
      case 'status':
        return activitiesCopy.sort((a, b) => {
          const statusOrder = { 'In Progress': 0, 'Completed': 1, 'Failed': 2 };
          const statusA = statusOrder[a.status as keyof typeof statusOrder] ?? 3;
          const statusB = statusOrder[b.status as keyof typeof statusOrder] ?? 3;
          return statusA - statusB;
        });
      
      default:
        return activitiesCopy;
    }
  }, [activities, selectedSort]);

  return {
    sortedActivities,
    sortOptions,
    selectedSort,
    setSelectedSort,
  };
};
